import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import TextField from "@mui/material/TextField";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDatePicker } from "@mui/x-date-pickers";

import CameraAltIcon from "@mui/icons-material/CameraAlt";

import moment from "moment-timezone";
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputBase,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PageShell from "components/bento/PageShell";
import BentoCard from "components/bento/BentoCard";
import { BG_TINT, BORDER, RADIUS_INPUT } from "components/bento/bentoTokens";
import {
  GetPackageDetailByTracking,
  GetPackagesByStationAndDate,
  PatchShippingFee,
  MarkPackageSelfOrder,
  scanPackage,
} from "api/packages";

import { GetStationDetailById } from "api/stations";
import { GetPendingArrivals } from "api/departureBatch";
import PendingArrivalDialog from "components/PendingArrivalDialog";
import Swal from "sweetalert2";
import {
  // compressImage,
  getScanText,
  playAlertSound,
  playBabySneezeSound,
  playCorrectSound,
  playIncorrectSound,
  playPoliceWhistleSound,
  playReadyBeebSound,
  playYahooSound,
  selectAndFocus,
  TRACKING_MAX_LENGTH,
} from "utils";
import useStationRefresh from "hooks/useStationRefresh";
import Scanner from "components/Scanner";
import { isMobile } from "react-device-detect";
import { GetRouteDetail, GetRouteList } from "api/routes";
import PackageNewDialog from "components/PackageNewDialog";
import PackageEditDialogForScan from "components/PackageEditDialogForScan";
import PackageEditDialog from "components/PackageEditDialog";
import PackageDataGrid from "components/PackageDataGrid";
import PhotoCaptureDialog from "components/PhotoCaptureDialog";
import FragileCheckDialog from "components/FragileCheckDialog";
import { uploadPackagePhoto, completeFragileCheck } from "api/packages";
import { getFileExtension } from 'utils/fileUtils'; // Add this import

const Scan = () => {
  const { t } = useTranslation();
  // Refresh the cached profile on entry so newly-assigned stations appear without
  // re-login; `stationList` is read reactively from the fresh user.
  const { user: refreshedUser, defaultStation: _defaultStation } = useStationRefresh();
  const stationList = refreshedUser?.stations || [];
  const [tracking, setTracking] = useState("");
  const [selectedStation, setSelectedStation] = useState(_defaultStation);
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [rows, setRows] = useState(null);
  const [successCount, setSuccessCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [newPackageDialogOpen, setNewPackageDialogOpen] = useState(false);
  const [editPackageDialogOpen, setEditPackageDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  // Full package editor opened from the "edit" column on the scans grid below —
  // separate from `PackageEditDialogForScan` above, which is a different,
  // scan-flow-specific dialog (no route/customer yet) triggered mid-scan.
  const [gridEditPackage, setGridEditPackage] = useState(null);

  // values to be sent to the backend
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  // const [sort, setSort] = useState({});
  const [search, setSearch] = useState("");
  const isInputByCameraScanner = useRef(false);

  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [pendingTracking, setPendingTracking] = useState(null);
  const [location, setLocation] = useState({ lat: null, lng: null });
  const [isPhotoUploading, setIsPhotoUploading] = useState(false); // Add this state
  const [uploadProgress, setUploadProgress] = useState(null);
  // Mandatory 2-step courier-label flow: null = not active (legacy single soft-photo
  // flow), 1 = courier shipping label step, 2 = package photo step.
  const [photoStep, setPhotoStep] = useState(null);

  // Fragile-check gate (409 FRAGILE_CHECK_REQUIRED from /package/scan). Photo-only
  // evidence — video is reported manually via chat group + manager, not uploaded.
  const [fragileDialogOpen, setFragileDialogOpen] = useState(false);
  const [fragileTracking, setFragileTracking] = useState(null);
  const [fragileStage, setFragileStage] = useState(null);
  const [isFragileUploading, setIsFragileUploading] = useState(false);
  const [fragileUploadProgress, setFragileUploadProgress] = useState(null);

  // Departure Batch: packages that left an origin station on a manifest but
  // haven't been scanned in yet at the currently-selected station — surfaced
  // as a counter chip so staff don't forget to scan something off the truck.
  const [pendingArrivals, setPendingArrivals] = useState([]);
  const [pendingArrivalDialogOpen, setPendingArrivalDialogOpen] = useState(false);

  function RefreshPendingArrivals(stationId) {
    if (!stationId) return;
    GetPendingArrivals(stationId).then((res) => {
      setPendingArrivals(res.success && Array.isArray(res.data) ? res.data : []);
    });
  }

  function RefreshPackagesList(from, to, station) {
    GetPackagesByStationAndDate(from, to, station).then((res) => {
      if (res.status === 200) {
        setRows(res.data);
      } else {
        console.log(res.message);
      }
    });
  }

  const handleGridEditClick = (row) => {
    GetPackageDetailByTracking(row.tracking).then((res) => {
      if (res.status === 200 && res.data) {
        setGridEditPackage(res.data);
      }
    });
  };

  // Once the refreshed default station lands, select it if nothing is selected yet
  // (preserves a station the operator has already picked).
  useEffect(() => {
    if (_defaultStation) setSelectedStation((prev) => prev || _defaultStation);
  }, [_defaultStation]);

  useEffect(() => {
    if (newPackageDialogOpen) playPoliceWhistleSound();
  }, [newPackageDialogOpen]);

  useEffect(() => {
    let newFromDate = moment(fromDate).format("YYYY-MM-DD");
    let newToDate = moment(toDate).format("YYYY-MM-DD");
    // console.log(`From: ${newFromDate} - To: ${newToDate}`);
    if (selectedStation) {
      RefreshPackagesList(newFromDate, newToDate, selectedStation.id);
      RefreshPendingArrivals(selectedStation.id);
    }
    selectAndFocus("tracking");
  }, [fromDate, toDate, selectedStation, successCount]);

  const scanPackageSubmited = (tracking) => {
    let newTracking = getScanText(tracking);
    let actionType = "scan";
    scanPackage(newTracking, selectedStation.id, actionType).then(
      (res) => {        
        if (res && res.success) {
          if (
            selectedStation.id === "VTE" &&
            res.data &&
            (res.data.routeId === "VTE-THK" ||
              res.data.routeId === "VTE-DKC")
          ) {
            playBabySneezeSound(); // to indicate that the scanned item need to be sent to THK branch
          } else {
            playCorrectSound(); // to indicate that the scanned item need to be sent to PHT branch
          }
          setTracking("");
          setSuccessCount(successCount + 1);
          Swal.fire({
            icon: "success",
            title: t("scan.successTitle"),
            text: t("scan.successText"),
            timer: 500,
          });
          // Prompt for photo
          if (selectedStation.isCourierLabelRequired) {
            // Mandatory 2-step flow: courier shipping label, then package photo.
            setPendingTracking(newTracking);
            setPhotoStep(1);
            setPhotoDialogOpen(true);
          } else if (selectedStation.isPhotoRequired) {
            setPendingTracking(newTracking);
            setPhotoStep(null);
            setPhotoDialogOpen(true);
          }

          if (isInputByCameraScanner.current) {
            setOpen(true);
            isInputByCameraScanner.current = false;
          }
        } else if (res && res.code === "FRAGILE_CHECK_REQUIRED") {
          playAlertSound();
          const trackingForCheck = res.data?.tracking || newTracking;
          Swal.fire({
            icon: "warning",
            title: t("scan.fragileCheck.alertTitle"),
            html: `<div style="color:#534EED; font-size: 20px; margin-bottom:8px; word-break: break-all;">${trackingForCheck}</div>
                   <ol style="text-align:left; display:inline-block; margin:0; padding-left: 20px;">
                     <li>${t("scan.fragileCheck.instructionPhotos")}</li>
                     <li>${t("scan.fragileCheck.instructionVideo")}</li>
                     <li>${t("scan.fragileCheck.instructionReport")}</li>
                   </ol>`,
            confirmButtonText: t("scan.fragileCheck.next"),
            allowOutsideClick: false,
            showCancelButton: false,
          }).then(() => {
            setFragileTracking(trackingForCheck);
            setFragileStage(res.stage);
            setFragileDialogOpen(true);
          });
        } else if (res.status === 202) {
          console.log(selectedStation);
          if (!selectedStation.allowPackageCreation) {
            playIncorrectSound();
            Swal.fire({
              title: t("scan.noticeTitle"),
              icon: "warning",
              text: res.message,
              timer: 2000,
            });
            if (isInputByCameraScanner.current) {
              setOpen(true);
              isInputByCameraScanner.current = false;
            } else {
              selectAndFocus("tracking");
            }
            return;
          }
          // In case data not found
          // playAlertSound();
          setNewPackageDialogOpen(true);

        } else {
          playIncorrectSound();
          Swal.fire({
            title: t("scan.scanFailedTitle"),
            icon: "error",
            text: res.message,
            timer: 2000,
          });
          if (isInputByCameraScanner.current) {
            setOpen(true);
            isInputByCameraScanner.current = false;
          } else {
            selectAndFocus("tracking");
          }
        }
      }
    );
  };

  const handleFormSubmit = (RawTracking) => {

    if (!RawTracking || RawTracking.trim() === "") {
      playAlertSound();
      Swal.fire({
        title: t("scan.requiredTitle"),
        icon: "warning",
        text: t("scan.requiredText"),
        showConfirmButton: true,
      });
      return;
    }

    let newTracking = getScanText(RawTracking);

    // Validate tracking number length
    if (newTracking.length < 8) {
      playAlertSound();
      Swal.fire({
        title: t("scan.tooShortTitle"),
        icon: "warning",
        html: `<div style="color:#E53E3E; font-size: 16px; margin-bottom:8px;">${newTracking}</div>
               <div>${t("scan.tooShortText", { count: newTracking.length })}</div>`,
        showConfirmButton: true,
      }).then(() => {
        selectAndFocus("tracking");
      });
      return;
    }

    setTracking(newTracking);

    GetPackageDetailByTracking(newTracking).then((res) => {
      console.log("🚀 ~ handleFormSubmit ~ res:", res)

      // Handle validation errors (400 status)
      if (res.status === 400) {
        console.log("🚀 ~ validation error from backend");
        playAlertSound();
        Swal.fire({
          title: t("scan.invalidTitle"),
          icon: "error",
          html: `<div style="color:#E53E3E; font-size: 16px; margin-bottom:8px;">${newTracking}</div>
                 <div>${res.message || t("scan.invalidText")}</div>`,
          showConfirmButton: true,
        }).then(() => {
          selectAndFocus("tracking");
        });
        return;
      }

      // if not found package in the system
      if (res.status === 202) {
        console.log("🚀 ~ not found package in the system");
        if (selectedStation.isTransitStation) {
          playAlertSound();
          Swal.fire({
            title: t("scan.notInSystemTitle"),
            icon: "warning",
            subtitle: `${tracking}`,
            html: `<div style="color:#534EED; font-size: 30px; margin-bottom:8px;">${tracking}</div>
         <div>${t("scan.notInSystemHint")}</div>`,
            showConfirmButton: true,
            showCancelButton: true,
            confirmButtonText: t("scan.confirm"),
            cancelButtonText: t("common.cancel"),
          }).then((result) => {
            if (result.isConfirmed) {
              scanPackageSubmited(tracking);
            }
          });
          return;
        } else if (selectedStation.allowPackageCreation) {
          setNewPackageDialogOpen(true);
          return;
        } else {
          playAlertSound();
          Swal.fire({
            title: t("scan.notFoundTitle"),
            icon: "warning",
            text: t("scan.notFoundText"),
            showConfirmButton: true,
          });
          return;
        }

      }

      // if found package in the system
      if (res.status === 200) {
        console.log("🚀 ~  found package in the system");
        // Package has already completed the whole delivery process (final state).
        // `station === "END"` is the terminal marker set on delivery — nothing more
        // to scan, so inform the operator and stop here.
        if (res.data.station === "END") {
          playAlertSound();
          Swal.fire({
            title: t("scan.routeDoneTitle"),
            icon: "info",
            html: `<div style="color:#534EED; font-size: 24px; margin-bottom:8px;">${res.data.tracking}</div>
                   <div>${t("scan.routeDoneBody")}</div>`,
            showConfirmButton: true,
          }).then(() => {
            selectAndFocus("tracking");
          });
          return;
        }
        if (res.data.station === selectedStation.id) {
          playAlertSound();
          Swal.fire({
            title: t("scan.alreadyScannedTitle"),
            icon: "warning",
            text: t("scan.alreadyScannedText", { tracking: res.data.tracking, station: res.data.station }),
            showConfirmButton: true,
          }).then(() => {
            selectAndFocus("tracking");
          });
          return;
        }
        if (res.data.validflag === false) {
          playAlertSound();
          Swal.fire({
            title: t("scan.disabledTitle"),
            icon: "warning",
            text: t("scan.disabledText", { tracking: res.data.tracking }),
            showConfirmButton: true,
          });
          selectAndFocus("tracking");
          return;
        }
        // Client-side stop for a cancelled parcel (the backend also rejects it with
        // PACKAGE_CANCELLED) — same friendly message as the mobile Scan screen.
        if (res.data.status === "Cancelled") {
          playAlertSound();
          Swal.fire({
            title: t("scan.cancelledTitle"),
            icon: "error",
            text: t("scan.cancelledText", { tracking: res.data.tracking }),
            showConfirmButton: true,
          });
          selectAndFocus("tracking");
          return;
        }

        if (res.data.routeId !== "N/A") {
          GetRouteDetail(res.data.routeId).then((routeResult) => {
            if (routeResult.status !== 200) {
              // playAlertSound();
              Swal.fire({
                title: t("scan.routeErrorTitle"),
                icon: "warning",
                text: t("scan.routeNotFound"),
                showConfirmButton: true,
              });
              selectAndFocus("tracking");
              return;
            }

            // Check if next station in route navigator matches selected station
            const routeData = routeResult.data;
            const currentPackageStation = res.data.station;

            if (routeData.navigator && routeData.navigator.length > 0) {
              // Find current station in navigator
              const currentStationInRoute = routeData.navigator.find(nav => nav.station === currentPackageStation);

              if (currentStationInRoute) {
                const nextStationInRoute = currentStationInRoute.nextStation;

                // Check if next station matches selected station
                if (nextStationInRoute !== selectedStation.id) {
                  playIncorrectSound();
                  Swal.fire({
                    title: t("scan.wrongStationTitle"),
                    icon: "warning",
                    html: `<div style="color:#E53E3E; font-size: 16px; margin-bottom:8px;">${res.data.tracking}</div>
                           <div>${t("scan.wrongStationShould")}: <strong>${nextStationInRoute}</strong></div>
                           <div>${t("scan.wrongStationScanningAt")}: <strong>${selectedStation.id}</strong></div>`,
                    showConfirmButton: true,
                    timer: 2000,
                  });
                  selectAndFocus("tracking");
                  return;
                }
              } else {
                // Current station not found in route navigator
                playAlertSound();
                Swal.fire({
                  title: t("scan.routeErrorTitle"),
                  icon: "warning",
                  text: t("scan.stationNotInRoute", { station: currentPackageStation }),
                  showConfirmButton: true,
                });
                selectAndFocus("tracking");
                return;
              }
            }

            // Route validation passed, continue with station-specific logic
            continueWithStationLogic(routeData);
          }).catch((error) => {
            console.error("Route validation error:", error);
            playAlertSound();
            Swal.fire({
              title: t("scan.errorTitle"),
              icon: "error",
              text: t("scan.routeFetchFailed"),
              showConfirmButton: true,
            });
            selectAndFocus("tracking");
            return
          });
        } else {
          // routeId === "N/A": no fixed route assigned. If the package still has a
          // current station, validate the scanned station against the next-stations of
          // EVERY route that passes through that station — so a wrong station is caught
          // before any customer/final-station logic runs.
          if (res.data.station) {
            GetRouteList()
              .then((routeListRes) => {
                const routes = (routeListRes && routeListRes.data) || [];
                const nextStations = [
                  ...new Set(
                    routes.flatMap((r) =>
                      (r.navigator || [])
                        .filter(
                          (n) =>
                            n.station === res.data.station &&
                            n.nextStation &&
                            n.nextStation !== "END"
                        )
                        .map((n) => n.nextStation)
                    )
                  ),
                ];

                if (
                  nextStations.length > 0 &&
                  !nextStations.includes(selectedStation.id)
                ) {
                  playIncorrectSound();
                  Swal.fire({
                    title: t("scan.wrongStationTitle"),
                    icon: "warning",
                    html: `<div style="color:#E53E3E; font-size: 16px; margin-bottom:8px;">${res.data.tracking}</div>
                           <div>${t("scan.wrongStationCurrent")}: <strong>${res.data.station}</strong></div>
                           <div>${t("scan.wrongStationOneOf")}: <strong>${nextStations.join(", ")}</strong></div>
                           <div>${t("scan.wrongStationScanningAt")}: <strong>${selectedStation.id}</strong></div>`,
                    showConfirmButton: true,
                  });
                  selectAndFocus("tracking");
                  return;
                }

                // Scanned station is a valid next station (or none defined) → proceed.
                continueWithStationLogic();
              })
              .catch((error) => {
                console.error("N/A-route station validation error:", error);
                // Don't block on a lookup failure — fall back to existing behavior.
                continueWithStationLogic();
              });
          } else {
            // No current station to validate against → existing behavior.
            continueWithStationLogic();
          }
        }

        // Move all station-specific logic into this function
        function continueWithStationLogic(prefetchedRouteData = null) {

          if (selectedStation.isTransitStation) {
            console.log("🚀 ~ selectedStation.isTransitStation")
            scanPackageSubmited(newTracking);
            return;
          }

          else if (selectedStation.isSortStation) {
            console.log("🚀 ~ selectedStation.isSortStation")
            // Customer is intentionally optional at a sort station — the box may have no
            // name on it. Only a missing route forces the edit dialog; re-scanning a
            // package that was saved with no customer must not reopen it again.
            if (res.data.routeId === "N/A") {
              playAlertSound();
              setEditPackageDialogOpen(true);
              setSelectedPackage(res.data);
              return;
            }
            // Reuse routeData already fetched during route validation — avoids a second API call.
            const routeData = prefetchedRouteData;

            // ask user to input shipping fee if it's a COD package or a fanshine route or a subcustomer and Shipping fee is 0
            if ((res.data.isCOD || routeData?.isFanshine || res.data.isSubCustomer) && parseInt(res.data?.shippingFee) === 0) {
                if (routeData?.isFanshine ||
                  parseInt(res.data.customer?.level) > 1
                ) {
                  playYahooSound(); // for special customers / agents that will get special discount
                } else {
                  playReadyBeebSound(); // for normal customers
                }

                let customerInfo = `${res.data.customer?.name} - ${res.data.customer?.whatsapp}`;
                let inputValue = "";

                Swal.fire({
                  title: t("scan.shippingFeePrompt.title"),
                  text: customerInfo,
                  input: "number",
                  inputAttributes: {
                    style:
                      "display: block; margin: 20px auto; text-align: center; width: 150px; height: 50px; padding: 10px; font-size: 30px;",
                  },
                  // inputValue,
                  preConfirm: () => {
                    inputValue = Swal.getInput().value;
                  },
                  inputValidator: (value) => {
                    if (!value) {
                      return t("scan.shippingFeePrompt.required");
                    } else if (isNaN(value)) {
                      return t("scan.shippingFeePrompt.invalid");
                    }
                    else if (value <= 0) {
                      return t("scan.shippingFeePrompt.mustBePositive");
                    }
                  },
                }).then((result) => {
                  if (result.isConfirmed) {
                    PatchShippingFee(
                      res.data._id,
                      res.data.tracking,
                      res.data.orderId,
                      res.data.description,
                      res.data.remark,
                      res.data.amount,
                      res.data.quantity,
                      inputValue
                    ).then((res) => {
                      if (res && res.success) {
                        scanPackageSubmited(newTracking);
                      } else {
                        Swal.fire({
                          title: t("scan.errorTitle"),
                          icon: "error",
                          text: t("scan.shippingFeePrompt.updateFailed"),
                          showConfirmButton: true,
                        });
                        console.log(res);
                        return;
                      }
                    });
                  }
                });

              }
              else {
                scanPackageSubmited(newTracking);
              }
          }
          else if (selectedStation.finalStation) {
            console.log("🚀 ~ selectedStation.finalStation")
            if (!res.data.customer) {
              playAlertSound();
              Swal.fire({
                title: t("scan.noCustomerTitle"),
                icon: "warning",
                text: t("scan.noCustomerText", { tracking: res.data.tracking }),
                showConfirmButton: true,
              });
              return;
            } else if (
              !res.data.isSelfOrder &&
              (!res.data.orderId || res.data.orderId === "")
            ) {
              playAlertSound();

              // Update package to set isSelfOrder = true (lightweight patch), plus the
              // shipping fee when it was just collected below.
              const applySelfOrder = (extraFields = {}) => {
                MarkPackageSelfOrder(res.data._id, extraFields.shippingFee)
                  .then((updateResult) => {
                    if (updateResult && (updateResult.status === 200 || updateResult.success)) {
                      Swal.fire({
                        icon: "success",
                        title: t("scan.selfOrderSavedTitle"),
                        text: t("scan.selfOrderSavedText"),
                        timer: 1000,
                        showConfirmButton: false
                      }).then(() => {
                        scanPackageSubmited(newTracking);
                      });
                    } else {
                      playIncorrectSound();
                      Swal.fire({
                        icon: "error",
                        title: t("scan.errorTitle"),
                        text: t("scan.selfOrderUpdateFailed"),
                        showConfirmButton: true
                      });
                    }
                  })
                  .catch((error) => {
                    playIncorrectSound();
                    console.error("Error updating package:", error);
                    Swal.fire({
                      icon: "error",
                      title: t("scan.errorTitle"),
                      text: t("scan.selfOrderUpdateFailed"),
                      showConfirmButton: true
                    });
                  });
              };

              // A self-order parcel has no order to price against, so a 0 shipping fee
              // means nothing has been charged for it yet — ask staff for the fee and
              // save it together with the self-order flag.
              const askSelfOrderShippingFee = () => {
                playReadyBeebSound();
                let inputValue = "";
                Swal.fire({
                  title: t("scan.selfOrderFee.title"),
                  text: `${res.data.customer?.name} - ${res.data.customer?.whatsapp}`,
                  input: "number",
                  inputAttributes: {
                    style:
                      "display: block; margin: 20px auto; text-align: center; width: 150px; height: 50px; padding: 10px; font-size: 30px;",
                  },
                  showCancelButton: true,
                  confirmButtonText: t("scan.selfOrderFee.confirm"),
                  cancelButtonText: t("common.cancel"),
                  preConfirm: () => {
                    inputValue = Swal.getInput().value;
                  },
                  inputValidator: (value) => {
                    if (!value) {
                      return t("scan.selfOrderFee.required");
                    } else if (isNaN(value)) {
                      return t("scan.selfOrderFee.invalid");
                    } else if (value <= 0) {
                      return t("scan.selfOrderFee.mustBePositive");
                    }
                  },
                }).then((feeResult) => {
                  if (feeResult.isConfirmed) {
                    applySelfOrder({ shippingFee: inputValue });
                  } else {
                    selectAndFocus("tracking");
                  }
                });
              };

              Swal.fire({
                title: t("scan.selfOrderTitle"),
                icon: "warning",
                text: t("scan.selfOrderText", { tracking: res.data.tracking }),
                showConfirmButton: true,
                showCancelButton: true,
                confirmButtonText: t("scan.selfOrderConfirm"),
                cancelButtonText: t("scan.selfOrderCancel"),
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
              }).then((result) => {
                if (result.isConfirmed) {
                  if (!(Number(res.data.shippingFee) > 0)) {
                    askSelfOrderShippingFee();
                  } else {
                    applySelfOrder();
                  }
                } else if (result.isDenied || result.dismiss === Swal.DismissReason.cancel) {
                  // User clicked "No" button specifically
                  playAlertSound();
                  Swal.fire({
                    title: t("scan.goMatchTitle"),
                    icon: "info",
                    text: t("scan.goMatchText"),
                    showConfirmButton: true
                  });
                  selectAndFocus("tracking");
                  return;
                }
                // If dismissed (closed without clicking any button), do nothing
              });
              return;
            }
            else {
              scanPackageSubmited(newTracking);
            }
          } else {
            scanPackageSubmited(newTracking);
          }
        } // Close continueWithStationLogic function
      }
    }).catch((error) => {
      // Network / server failure while looking the parcel up — show it instead of an
      // unhandled rejection (and never treat it as "not found").
      console.error("Package lookup error:", error);
      playIncorrectSound();
      Swal.fire({
        title: t("scan.errorTitle"),
        icon: "error",
        text: error?.message || t("scan.unknownError"),
        showConfirmButton: true,
      });
      selectAndFocus("tracking");
    });
  }

  // Handle photo capture and upload - UPDATED WITH LOADING STATE
  const handlePhotoCapture = async (file) => {
    if (!pendingTracking || !file) return;

    setIsPhotoUploading(true); // Start loading
    setUploadProgress(null);
    const category =
      photoStep === 1 ? "courier_label" : photoStep === 2 ? "package_photo" : undefined;

    try {
      const ext = getFileExtension(file);

      console.log('File info:', {
        name: file.name || 'No name',
        type: file.type || 'No type',
        size: file.size || 0,
        extension: ext
      });

      await uploadPackagePhoto(pendingTracking, file, ext, location, category, setUploadProgress, selectedStation?.id);

      Swal.fire({
        icon: "success",
        title: "Photo uploaded!",
        timer: 500,
        showConfirmButton: false,
      });
      playCorrectSound();

      if (photoStep === 1) {
        // Mandatory flow: advance to the package-photo step, keep the dialog open.
        setPhotoStep(2);
      } else {
        setPhotoDialogOpen(false);
        setPendingTracking(null);
        setPhotoStep(null);
      }
    } catch (err) {
      console.error('Photo upload error:', err);
      Swal.fire({
        icon: "error",
        title: "Photo upload failed",
        text: err.message || 'Unknown error occurred',
      });
      playIncorrectSound();
      if (photoStep === null) {
        // Legacy single-photo flow: abandon on failure, same as before.
        setPhotoDialogOpen(false);
        setPendingTracking(null);
      }
      // Mandatory flow: leave the dialog open on the same step so staff must retry.
    } finally {
      setIsPhotoUploading(false); // End loading
      setUploadProgress(null);
    }
  };

  // Upload fragile-check photo evidence, then close the dialog and re-run the scan —
  // the backend gate now sees a matching fragileChecks entry and lets the scan through.
  const handleFragileCheckSubmit = async (files, videoReportConfirmed) => {
    if (!fragileTracking || !fragileStage || !selectedStation) return;

    setIsFragileUploading(true);
    setFragileUploadProgress(null);
    try {
      await completeFragileCheck(
        fragileTracking,
        fragileStage,
        selectedStation.id,
        files,
        location,
        videoReportConfirmed,
        setFragileUploadProgress
      );
      setFragileDialogOpen(false);
      setFragileTracking(null);
      setFragileStage(null);
      playCorrectSound();
      Swal.fire({
        icon: "success",
        title: "Fragile check saved!",
        timer: 500,
        showConfirmButton: false,
      });
      scanPackageSubmited(fragileTracking);
    } catch (err) {
      console.error("Fragile check upload error:", err);
      playIncorrectSound();
      Swal.fire({
        icon: "error",
        title: "Fragile check upload failed",
        text: err?.response?.data?.message || err.message || "Unknown error occurred",
      });
      // Leave the dialog open so staff must retry — the check is still mandatory.
    } finally {
      setIsFragileUploading(false);
      setFragileUploadProgress(null);
    }
  };

  return (
    <PageShell title="Scan package">
      <BentoCard sx={{ mb: "22px" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", xl: "repeat(4, 1fr)" },
            gap: "16px",
            alignItems: "start",
          }}
        >
          <Box sx={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
            <FormControl fullWidth>
              <InputLabel>Select station</InputLabel>
              <Select
                label="Select station"
                value={selectedStation ? selectedStation.id : ""}
                onChange={(e) => {
                  GetStationDetailById(e.target.value).then((res) => {
                    if (res && res.success) {
                      setSelectedStation(res.data);
                      selectAndFocus("tracking");
                      // console.log("Select station", res.data);
                    }
                  });
                }}
                required
              >
                {stationList &&
                  stationList.map(({ id, name }) => {
                    return (
                      <MenuItem key={id} value={id}>
                        {id} ({name})
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
            {pendingArrivals.length > 0 && (
              <Chip
                icon={<HourglassEmptyIcon />}
                label={t("scan.pendingArrival.chipLabel", { count: pendingArrivals.length })}
                color="warning"
                onClick={() => setPendingArrivalDialogOpen(true)}
                sx={{ mt: "8px", flexShrink: 0, cursor: "pointer" }}
              />
            )}
          </Box>
          <FormControl fullWidth>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                background: BG_TINT,
                border: `1px solid ${BORDER}`,
                borderRadius: RADIUS_INPUT,
                gap: 0,
                p: "0.5rem 1rem",
              }}
            >
              <InputBase
                placeholder="Tracking number"
                id="tracking"
                autoFocus
                value={tracking}
                inputProps={{ maxLength: TRACKING_MAX_LENGTH }}
                onChange={(e) => setTracking(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleFormSubmit(e.target.value);
                    e.preventDefault(); // Prevent the default tab behavior
                  }
                }}
                type="text" // Set the input type to text
                sx={{ flex: 1 }}
              />
              <Box display="flex">
                <IconButton onClick={() => handleFormSubmit(tracking)} sx={{ p: 1 }}>
                  <SearchIcon />
                </IconButton>
                {isMobile && (
                  <IconButton onClick={() => setOpen(true)} sx={{ p: 1 }}>
                    <CameraAltIcon />
                  </IconButton>
                )}
              </Box>
            </Box>
          </FormControl>
          <FormControl fullWidth>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DesktopDatePicker
                label="Filter From"
                inputFormat="DD/MM/yyyy"
                value={fromDate}
                maxDate={toDate}
                onChange={(newValue) => setFromDate(newValue)}
                renderInput={(params) => <TextField {...params} />}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </FormControl>
          <FormControl fullWidth>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DesktopDatePicker
                label="Filter To"
                inputFormat="DD/MM/yyyy"
                minDate={fromDate}
                value={toDate}
                onChange={(newValue) => setToDate(newValue)}
                renderInput={(params) => <TextField {...params} />}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </FormControl>
        </Box>
      </BentoCard>

      {/*  Data Grid  */}
      <PackageDataGrid
        packages={rows || []}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        search={search}
        setSearch={setSearch}
        onEdit={handleGridEditClick}
      />

      {/* Pause Scanner when photo dialog is open */}
      {!photoDialogOpen && !newPackageDialogOpen && (
        <Scanner
          open={open}
          setOpen={setOpen}
          onDecode={(result) => {
            console.log("🚀 ~ result:", result)
            if (result) {
              isInputByCameraScanner.current = true;
              handleFormSubmit(result);
            }
          }}
        />
      )}

      {/* Package New Dialog  */}
      <PackageNewDialog
        onClose={() => {
          setNewPackageDialogOpen(false);
        }}
        successCount={successCount}
        setSuccessCount={setSuccessCount}
        open={newPackageDialogOpen}
        stationObj={selectedStation}
        trackingNumber={tracking}
        disableTrackingNumber={true}
      />
      <PackageEditDialogForScan
        open={editPackageDialogOpen}
        onClose={() => {
          setEditPackageDialogOpen(false);
        }}
        onSuccess={() => {
          handleFormSubmit(tracking);
          setEditPackageDialogOpen(false);
        }}
        packageData={selectedPackage}
        selectedStation={selectedStation}
      />

      <PhotoCaptureDialog
        open={photoDialogOpen}
        onClose={() => {
          if (photoStep !== null) return; // Mandatory flow: can't be dismissed early.
          setPhotoDialogOpen(false);
        }}
        onCapture={handlePhotoCapture}
        tracking={pendingTracking}
        location={location}
        setLocation={setLocation}
        isUploading={isPhotoUploading} // Add this prop
        uploadProgress={uploadProgress}
        hideClose={photoStep !== null}
        title={
          photoStep === 1
            ? "Photo 1 of 2: Courier shipping label (Lazada/Shopee/etc.)"
            : photoStep === 2
            ? "Photo 2 of 2: Photo of the package"
            : undefined
        }
      />

      <FragileCheckDialog
        open={fragileDialogOpen}
        onSubmit={handleFragileCheckSubmit}
        tracking={fragileTracking}
        stage={fragileStage}
        location={location}
        setLocation={setLocation}
        isUploading={isFragileUploading}
        uploadProgress={fragileUploadProgress}
      />

      <PendingArrivalDialog
        open={pendingArrivalDialogOpen}
        onClose={() => setPendingArrivalDialogOpen(false)}
        pendingArrivals={pendingArrivals}
        onChanged={() => RefreshPendingArrivals(selectedStation?.id)}
      />

      <PackageEditDialog
        open={!!gridEditPackage}
        onClose={() => setGridEditPackage(null)}
        packageData={gridEditPackage}
        onSuccess={() => {
          setGridEditPackage(null);
          RefreshPackagesList(
            moment(fromDate).format("YYYY-MM-DD"),
            moment(toDate).format("YYYY-MM-DD"),
            selectedStation?.id
          );
        }}
      />
    </PageShell>
  );
};

export default Scan;